import {
  Box,
  Button,
  Flex,
  FormErrorMessage,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  Link,
  Stack,
  StackDivider,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import React, { useEffect, useState } from 'react';
import logo from '../assets/image/logo.png';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser } from '../stores/slices/user/userActions';

const schema = yup
  .object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    organisationName: yup.string().required('Organisation name is required'),
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required'),
    password: yup
      .string()
      .min(8, 'Must be at least 8 characters long')
      .required('Password is required'),
    confirmPassword: yup
      .string()
      .required('Please retype your password.')
      .oneOf([yup.ref('password')], 'Your passwords do not match.'),
  })
  .required();

const Signup = () => {
  const { userToken, error, success } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const [errorList, setErrorList] = useState([]);
  useEffect(() => {
    if (success) reset();

    // redirect authenticated user to dashboard
    if (userToken) navigate('/dashboard');
  }, [userToken, success, navigate, reset]);

  const onSubmit = async data => {
    const res = await dispatch(registerUser(data));
    if (res.type === 'user/register/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast({
        description:
          'A confirmation email has been sent to your nominated email address. Please follow the instructions there to activate your account',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  useEffect(() => {
    if (error) {
      let list = [];
      Object.keys(error).forEach((key, index) => {
        list.push(
          `${key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')} ${
            error[key]
          }`
        );
      });
      setErrorList(list.join('<br />'));
    }
  }, [error]);

  return (
    <Stack spacing={8} mx={'auto'} maxW={'6xl'} w={'100%'} py={12} px={6}>
      <Box rounded={'lg'} bg="white" boxShadow={'lg'}>
        <Stack
          spacing={4}
          direction={{ base: 'column', md: 'row' }}
          justify={'space-between'}
          align={'center'}
          divider={<StackDivider borderColor="gray.200" />}
        >
          <Box p={8}>
            <Image
              src={logo}
              alt="4 boxes logo"
              boxSize="300px"
              objectFit="contain"
            />
          </Box>
          <Box p={8} w={'100%'} maxW={'2xl'} mx="auto">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box>
                <Heading mb="10px">Sign Up</Heading>
                <Text
                  fontSize={'md'}
                  mb="4"
                  ms="4px"
                  color="primary"
                  fontWeight="500"
                >
                  Start your 14 day trial with us
                </Text>
                {error && (
                  <Box
                    bg={'red.200'}
                    p={4}
                    borderRadius="lg"
                    color={'white'}
                    mb={4}
                  >
                    {errorList}
                  </Box>
                )}
              </Box>
              <Stack>
                <Stack
                  spacing={4}
                  direction={{ base: 'column', md: 'row' }}
                  justify={'space-between'}
                  align={'flex-start'}
                >
                  <FormControl id="firstName" isInvalid={errors.firstName}>
                    <FormLabel htmlFor="firstName">First Name</FormLabel>
                    <Input id="firstName" {...register('firstName')} />
                    <FormErrorMessage>
                      {errors.firstName && errors.firstName.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl id="lastName" isInvalid={errors.lastName}>
                    <FormLabel htmlFor="lastName">Last Name</FormLabel>
                    <Input id="lastName" {...register('lastName')} />
                    <FormErrorMessage>
                      {errors.lastName && errors.lastName.message}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
                <Stack
                  spacing={4}
                  direction={{ base: 'column', md: 'row' }}
                  justify={'space-between'}
                  align={'center'}
                >
                  <FormControl id="email" isInvalid={errors.email}>
                    <FormLabel htmlFor="email">Email address</FormLabel>
                    <Input id="email" {...register('email')} />
                    <FormErrorMessage>
                      {errors.email && errors.email.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl
                    id="organisationName"
                    isInvalid={errors.organisationName}
                  >
                    <FormLabel htmlFor="organisationName">
                      Organisation Name
                    </FormLabel>
                    <Input
                      id="organisationName"
                      {...register('organisationName')}
                    />
                    <FormErrorMessage>
                      {errors.organisationName &&
                        errors.organisationName.message}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
                <Stack
                  spacing={4}
                  direction={{ base: 'column', md: 'row' }}
                  justify={'space-between'}
                  align={'center'}
                >
                  <FormControl id="password" isInvalid={errors.password}>
                    <FormLabel htmlFor="password">Password</FormLabel>
                    <Input
                      type="password"
                      id="password"
                      {...register('password')}
                    />
                    <FormErrorMessage>
                      {errors.password && errors.password.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl
                    id="confirmPassword"
                    isInvalid={errors.confirmPassword}
                  >
                    <FormLabel htmlFor="confirmPassword">
                      Password Confirmation
                    </FormLabel>
                    <Input
                      type="password"
                      id="confirmPassword"
                      {...register('confirmPassword')}
                    />
                    <FormErrorMessage>
                      {errors.confirmPassword && errors.confirmPassword.message}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
                <Stack spacing={4}>
                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    alignItems={'start'}
                    justifyContent={'start'}
                  >
                    <Link as={RouterLink} to="/forgot-password">
                      <Text color={'primary'} as="span" ms="5px">
                        Forgot Password?
                      </Text>
                    </Link>
                  </Stack>
                  <Button isLoading={isSubmitting} type="submit">
                    Sign up
                  </Button>
                </Stack>
                <Flex
                  direction="column"
                  justifyContent="center"
                  alignItems="start"
                  maxW="100%"
                  mt="0px"
                >
                  <Text fontWeight="400" fontSize="14px">
                    Registered already?
                    <Link as={RouterLink} to="/">
                      <Text
                        color={'primary'}
                        as="span"
                        ms="5px"
                        fontWeight="500"
                      >
                        Login
                      </Text>
                    </Link>
                  </Text>
                </Flex>
                <Text fontWeight="400" fontSize="14px">
                  <a
                    href="https://oasispc.com.au/privacy-policy"
                    target="_blank"
                  >
                    <Text color={'primary'} as="span">
                      Privacy Policy
                    </Text>
                  </a>
                </Text>
              </Stack>
            </form>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

export default Signup;
